import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Search, X } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Pill } from '../componentes/ui/Pill.js';
import { useSessao } from '../auth/SessaoContexto.js';

interface Endereco {
  ip: string;
  ptr: string;
  blocoId: string;
  cidr: string;
  operadora: string;
  versao: 'V4' | 'V6';
}

/**
 * Visão única de todos os reversos configurados, com edição no lugar.
 *
 * É o caminho curto: quem opera reverso quase sempre chega com o endereço na
 * mão, e antes precisava adivinhar a qual bloco ele pertencia, abrir aquele
 * bloco e paginar até achar. Aqui filtra e edita sem sair da linha.
 */
export function GerenciarIps() {
  const qc = useQueryClient();
  const { pode } = useSessao();
  const [params, setParams] = useSearchParams();
  const [filtro, setFiltro] = useState(params.get('ip') ?? '');
  const [debounce, setDebounce] = useState(filtro);

  // A busca no servidor lê todas as zonas do PowerDNS; disparar a cada tecla
  // multiplicaria isso por caractere digitado.
  useEffect(() => {
    const t = setTimeout(() => setDebounce(filtro), 300);
    return () => clearTimeout(t);
  }, [filtro]);

  const { data: enderecos = [], isLoading } = useQuery({
    queryKey: ['enderecos', debounce],
    queryFn: () =>
      api.get<Endereco[]>(`/busca/enderecos${debounce ? `?q=${encodeURIComponent(debounce)}` : ''}`),
  });

  const podeEditar = pode('ptr:escrever');
  const publicaDireto = pode('ptr:publicar-direto');

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-texto-mudo"
        />
        <input
          value={filtro}
          onChange={(e) => {
            setFiltro(e.target.value);
            setParams(e.target.value ? { ip: e.target.value } : {}, { replace: true });
          }}
          autoFocus
          placeholder="Filtrar por endereço ou nome do reverso…"
          className="h-12 w-full rounded-card border border-borda bg-superficie pl-10 pr-10 font-mono text-[14px] placeholder:font-sans placeholder:text-texto-mudo focus:border-marca focus:outline-none"
        />
        {filtro && (
          <button
            onClick={() => {
              setFiltro('');
              setParams({}, { replace: true });
            }}
            aria-label="Limpar"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-texto-mudo hover:text-texto"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {!publicaDireto && podeEditar && (
        <p className="rounded-card border border-aviso/20 bg-aviso-fundo px-4 py-2.5 text-[12.5px] text-aviso">
          Suas alterações passam por aprovação. Edite pelo bloco para enviar a solicitação.
        </p>
      )}

      {isLoading ? (
        <Esqueleto />
      ) : enderecos.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie px-6 py-12 text-center">
          <Search size={22} className="mx-auto mb-2 text-texto-mudo" />
          <p className="text-[14px] font-bold">
            {debounce ? 'Nenhum endereço encontrado' : 'Nenhum reverso configurado ainda'}
          </p>
          <p className="mx-auto mt-1 max-w-[420px] text-[13px] text-texto-suave">
            {debounce
              ? 'Esta tela lista apenas endereços que já têm reverso. Para configurar um novo, abra o bloco.'
              : 'Assim que houver reversos configurados, eles aparecem aqui — de todos os blocos, num lugar só.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Endereço', 'Reverso', 'Bloco', 'Operadora', ''].map((h) => (
                    <th
                      key={h}
                      className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enderecos.map((e) => (
                  <Linha
                    key={e.ip}
                    endereco={e}
                    editavel={podeEditar && publicaDireto}
                    aoSalvar={() => void qc.invalidateQueries({ queryKey: ['enderecos'] })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {enderecos.length > 0 && (
        <p className="text-center text-[12px] text-texto-mudo">
          {enderecos.length} {enderecos.length === 1 ? 'endereço' : 'endereços'} com reverso
        </p>
      )}
    </div>
  );
}

/** Edição no lugar: sem modal, sem trocar de tela. */
function Linha({
  endereco: e,
  editavel,
  aoSalvar,
}: {
  endereco: Endereco;
  editavel: boolean;
  aoSalvar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(e.ptr);

  const salvar = useMutation({
    mutationFn: () =>
      api.put(`/blocos/${e.blocoId}/ptr`, { ip: e.ip, ptr: valor.trim() || null }),
    onSuccess: () => {
      setEditando(false);
      aoSalvar();
    },
  });

  return (
    <tr className="border-t border-borda-suave hover:bg-borda-tenue">
      <td className="px-[18px] py-[11px] font-mono text-[13px] font-semibold">{e.ip}</td>

      <td className="px-[18px] py-[11px]">
        {editando ? (
          <div className="flex gap-2">
            <input
              value={valor}
              onChange={(ev) => setValor(ev.target.value)}
              autoFocus
              spellCheck={false}
              onKeyDown={(ev) => {
                // Enter salva e Esc cancela: numa tabela de edição rápida,
                // obrigar o mouse a cada linha desperdiça o ganho.
                if (ev.key === 'Enter') salvar.mutate();
                if (ev.key === 'Escape') {
                  setValor(e.ptr);
                  setEditando(false);
                }
              }}
              className="h-8 flex-1 rounded-chip border border-marca px-2 font-mono text-[12.5px] focus:outline-none"
            />
            <button
              onClick={() => salvar.mutate()}
              disabled={salvar.isPending}
              aria-label="Salvar"
              className="grid size-8 shrink-0 place-items-center rounded-chip bg-marca text-white hover:bg-marca-hover disabled:opacity-50"
            >
              <Check size={15} />
            </button>
            <button
              onClick={() => {
                setValor(e.ptr);
                setEditando(false);
              }}
              aria-label="Cancelar"
              className="grid size-8 shrink-0 place-items-center rounded-chip border border-borda text-texto-suave"
            >
              <X size={15} />
            </button>
          </div>
        ) : (
          <span className="font-mono text-[13px]">{e.ptr}</span>
        )}

        {salvar.error && (
          <p className="mt-1 text-[11.5px] font-semibold text-erro">
            {(salvar.error as ApiError).mensagem}
          </p>
        )}
      </td>

      <td className="px-[18px] py-[11px]">
        <Pill tom={e.versao === 'V6' ? 'ipv6' : 'info'} mono>
          {e.cidr}
        </Pill>
      </td>
      <td className="px-[18px] py-[11px] text-[12.5px] text-texto-suave">{e.operadora}</td>
      <td className="px-[18px] py-[11px] text-right">
        {editavel && !editando && (
          <Botao tamanho="tabela" onClick={() => setEditando(true)}>
            Editar
          </Botao>
        )}
      </td>
    </tr>
  );
}

function Esqueleto() {
  return (
    <div className="rounded-card border border-borda bg-superficie p-4">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-11 border-b border-borda-suave last:border-0">
          <div className="h-3 w-1/4 translate-y-4 animate-pulse rounded bg-borda" />
        </div>
      ))}
    </div>
  );
}
